import styled from '@emotion/styled';
import React, { ReactNode, useState } from 'react';

import { useLocale } from 'client/i18n/hooks/useLocale';
import { GOOGLE_LOCALE_MAPPING } from 'client/i18n/localeMappings';
import { EmbeddedYoutubeVideo } from '../../EmbeddedYoutubeVideo';
import { DEFAULT_DROP_SHADOW } from '../styles';
import { MarketingVideoPlayIcon } from './MarketingVideoPlayIcon';
import { localeCodeMapping } from '../constants';

const MarketingVideoContainer = styled.button`
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  position: relative;

  &:focus {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: black;
      opacity: 0.2;
      z-index: 0;
    }
  }
`;

const MarketingVideoWrapper = styled.div<{ isDropShadowed: boolean }>`
  box-shadow: ${({ isDropShadowed }) =>
    isDropShadowed ? DEFAULT_DROP_SHADOW : 0};
`;

const MarketingVideoIconContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const MarketingVideo = ({
  width,
  height,
  youTubeVideoId,
  title,
  children,
  isDropShadowed = true,
}: {
  width: number;
  height: number;
  youTubeVideoId: string;
  children: ReactNode;
  title: string;
  isDropShadowed?: boolean;
}) => {
  const { locale } = useLocale();
  const [showVideo, toggleVideo] = useState(false);
  const ratio = height / width;

  if (showVideo)
    return (
      <MarketingVideoWrapper isDropShadowed={isDropShadowed}>
        <EmbeddedYoutubeVideo
          title={title}
          youtubeVideoId={youTubeVideoId}
          paddingBottomOffset={`${Math.floor(ratio * 100)}%`}
          youtubeEmbedParameters={{
            autoplay: 1,
            controls: 1,
            modestbranding: 1,
            loop: 1,
            // eslint-disable-next-line camelcase
            cc_load_policy: 1,
            mute: 1,
            rel: 0,
            ...(locale &&
              locale !== 'en' && {
                hl:
                  (GOOGLE_LOCALE_MAPPING as localeCodeMapping)[locale] ||
                  locale,
                // eslint-disable-next-line camelcase
                cc_lang_pref: locale.substring(0, 2),
              }),
          }}
        />
      </MarketingVideoWrapper>
    );
  return (
    <MarketingVideoContainer
      onClick={() => toggleVideo(true)}
      aria-label={`Play ${title}`}
    >
      {children}
      <MarketingVideoIconContainer>
        <MarketingVideoPlayIcon width="max(48px, 12%)" />
      </MarketingVideoIconContainer>
    </MarketingVideoContainer>
  );
};
